import { createSelector } from '@reduxjs/toolkit';

import { calculateTrussSolidWithCache } from '@paragon/layout';
import { notNullish } from '@paragon/utilities';

import { getTrussSolidCalculationDependencies } from '../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import {
  aggregateErrors,
  formatDesignEnvelopeError,
  FormattedError,
  shouldIgnoreServerGeneratedError,
} from '../../library/layoutErrors';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { RootState } from '..';

export const getSingleTrussEnvelopeErrorCalculator = createSelector(
  (state: RootState) => state.layout.trussEnvelopes,
  (state: RootState) => state.layout.trussEnvelopeErrors,
  getTrussSolidCalculationDependencies,
  (trussEnvelopes, serverErrors, dependencies) =>
    (trussEnvelopeGuid: string): FormattedError | null => {
      const trussEnvelope = trussEnvelopes[trussEnvelopeGuid];
      if (!trussEnvelope) return null;

      // If the truss envelope is linked to a design, showing an error could cause confusion
      if (trussEnvelope.componentDesignGuid != null) return null;

      // If the server reported an error, it takes precedence over any clientside error unless it should be ignored
      const serverError = serverErrors[trussEnvelopeGuid];
      if (serverError && !shouldIgnoreServerGeneratedError(serverError))
        return formatDesignEnvelopeError(trussEnvelope, serverError);

      // If the client calculation encountered an error, display it
      const clientError = calculateTrussSolidWithCache(trussEnvelope, dependencies).err;
      if (clientError) return formatDesignEnvelopeError(trussEnvelope, { type: 'TrussSolid', error: clientError });

      return null;
    },
);

export const getTrussEnvelopeErrorForSelectedEnvelopes = createSelector(
  getSelectedItemIdentifiers,
  getSingleTrussEnvelopeErrorCalculator,
  (selectedItemIdentifiers, errorCalculator): FormattedError | null => {
    const selectedTrussEnvelopeGuids = selectedItemIdentifiers
      .filter(({ type }) => type === 'trussEnvelope')
      .map(({ guid }) => guid);
    const otherItemsSelected = selectedItemIdentifiers.length !== selectedTrussEnvelopeGuids.length;
    if (otherItemsSelected) return null;

    const errors = selectedTrussEnvelopeGuids
      .map((trussEnvelopeGuid) => errorCalculator(trussEnvelopeGuid))
      .filter(notNullish);
    return aggregateErrors(errors);
  },
);
